import React, { useRef, useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import CustomFeedDrawer from "./CustomFeedDrawer";
import SinglePropertyFeed from "../SinglePropertyFeed/SinglePropertyFeed";
import PropertyFeedSkeleton from "../PropertyFeedSkeleton";
import { getListingFeedProperties, getPropertyByLinkCode } from "@/services/PropertyService";
import { getUserFeeds } from "@/services/UserFeedService";
import { useUser } from "@/context/UserContext";
import CustomSvg from "@/components/common/custom-svg/custom-svg";
import addFirstFeedIcon from "@/assets/icons-v2/add_first_feed.png";
import useCustomLoginWithRedirect from "@/hooks/useCustomLoginWithRedirect";
import { THIRTY_MINUTES } from "@/constants/time";
import { useFeedAnimation } from "@/context/FeedAnimationContext";

const INITIAL_LIMIT = 10;
const CACHE_TIME = THIRTY_MINUTES; // 30 minutes

/**
 * CustomFeed component displays a list of properties that the user is following
 * @param {Object} props - Component props
 * @param {boolean} props.show - Controls the visibility of the CustomFeedDrawer
 * @param {Function} props.onHide - Callback function to hide the CustomFeedDrawer
 * @returns {JSX.Element} CustomFeed component
 */
interface CustomFeedProps {
    show: boolean;
    onHide: () => void;
}

const CustomFeed = ({ show, onHide }: CustomFeedProps) => {
    const { userState } = useUser();
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const { isAuthenticated } = useAuth0();
    const loaderRef = useRef<HTMLDivElement | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { showSuccessAnimation, showDeleteSuccessAnimation } = useFeedAnimation();
    const shouldShowDrawer = (show || drawerOpen) && !showSuccessAnimation && !showDeleteSuccessAnimation;

    const {
        data: feedsResponse = { data: [] as any[] },
        isLoading: feedsLoading,
        error: feedsError,
    } = useQuery({
        queryKey: ["userFeeds", userState?.id],
        queryFn: () => getUserFeeds(userState?.id),
        enabled: !!userState?.id,
        refetchOnWindowFocus: true,
        staleTime: 0,
    });
    const feeds = (feedsResponse as any)?.data || [];
    const hasFeeds = Array.isArray(feeds) && feeds.length > 0;

    // Only enable property query if feeds exist
    const {
        data,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        error: propertiesError,
    } = useInfiniteQuery({
        queryKey: ["live-variant-customFeed-properties", userState?.id],
        queryFn: ({ pageParam = 1 }) =>
            getListingFeedProperties({
                page: String(pageParam),
                limit: String(INITIAL_LIMIT),
                userId: userState?.id ?? null,
                userFeedPreferenceId: "", // Empty for now to fetch all user feed preference
                feedType: "following",
            }),
        getNextPageParam: (lastPage: any, allPages: any[]) => {
            if (lastPage?.data?.hasNextPage && allPages && Array.isArray(allPages)) {
                return allPages.length + 1;
            }
            return undefined;
        },
        initialPageParam: 1,
        staleTime: 0,
        gcTime: CACHE_TIME,
        enabled: hasFeeds, // Only run if feeds exist
    });

    // Toast property fetch errors when feeds exist
    useEffect(() => {
        if (hasFeeds && propertiesError) {
            const err: any = propertiesError as any;
            toast.error(err?.response?.data?.message || err?.message || "An error occurred.");
        }
    }, [hasFeeds, propertiesError]);

    // Extract plc parameter from URL
    const [searchParams] = useSearchParams();
    const plc = searchParams.get("plc");

    // Query for top property if plc parameter exists
    const { data: topPropertyData, error: topPropertyError } = useQuery({
        queryKey: ["top-property", plc],
        queryFn: () => getPropertyByLinkCode({ property_link_code: plc as string }),
        enabled: !!plc,
        staleTime: THIRTY_MINUTES,
        gcTime: THIRTY_MINUTES,
    });

    // Handle top property error
    useEffect(() => {
        if (topPropertyError) {
            console.error("Error fetching top property:", topPropertyError);
            toast.error("Could not load the specific property. Showing following properties instead.");
        }
    }, [topPropertyError]);

    // Combine top property with following properties
    const topData = (topPropertyData as any)?.data;
    const combinedProperties = React.useMemo(() => {
        if (!data?.pages) return [] as any[];
        const allProperties = (data.pages as any[]).flatMap((page: any) => page.data?.data) || [];
        if (topData) {
            const filteredProperties = allProperties.filter((property: any) => property.id !== topData.id);
            return [topData, ...filteredProperties];
        }
        return allProperties;
    }, [topData, data?.pages]);

    const handleAddFeedClick = () => {
        if (!isAuthenticated) {
            customLoginWithRedirect({
                authorizationParams: {
                    redirect_uri: window.location.origin + "/social",
                },
                appState: {
                    toggleProfile: true,
                    version: "social",
                    showSetNicknameModal: true,
                    event: {
                        name: "tutorial_register_click",
                        referral_code: localStorage.getItem("referral_code"),
                    },
                },
            });
            return;
        } else {
            // If authenticated, open the drawer directly
            setDrawerOpen(true);
        }
    };

    useEffect(() => {
        if (!hasFeeds) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { threshold: 1.0 },
        );

        const currentLoader = loaderRef.current;
        if (currentLoader) observer.observe(currentLoader);
        return () => {
            if (currentLoader) observer.unobserve(currentLoader);
        };
    }, [hasFeeds, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Ensure drawerOpen is always false when a success or delete animation is showing
    useEffect(() => {
        if (showSuccessAnimation || showDeleteSuccessAnimation) {
            setDrawerOpen(false);
        }
    }, [showSuccessAnimation, showDeleteSuccessAnimation]);

    if (isAuthenticated) {
        // Show loading for feeds
        if (feedsLoading) {
            return (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
                    <PropertyFeedSkeleton />
                </div>
            );
        }

        // Show error for feeds
        if (feedsError) {
            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-3/4 tw-text-center tw-text-gray-500 tw-p-8">
                    <div className="tw-mb-4 tw-text-red-500">
                        {(feedsError as any)?.response?.data?.message ||
                            (feedsError as any)?.message ||
                            "Failed to load feeds."}
                    </div>
                </div>
            );
        }
    }

    // If no feeds, show Add your first Feed screen
    if (!hasFeeds || !isAuthenticated) {
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-pt-16">
                <CustomSvg src={addFirstFeedIcon} size={{ width: 90, height: 90 }} className="tw-mb-6" />
                <div className="tw-font-bold tw-text-xl tw-mb-2">Add your first Feed</div>
                <div className="tw-text-gray-500 tw-mb-6 tw-text-center" style={{ maxWidth: 320 }}>
                    Keep tabs on homes near you, plan your next move, or find the home from your fantasies
                </div>
                <button
                    className="tw-bg-orange-400 tw-text-white tw-rounded-lg tw-px-8 tw-py-3 tw-font-semibold tw-text-base hover:tw-bg-orange-500 focus:tw-outline-none"
                    onClick={() => handleAddFeedClick()}
                >
                    Add a feed
                </button>
                {shouldShowDrawer && (
                    <CustomFeedDrawer show={true} onHide={() => setDrawerOpen(false)} feeds={feeds as any[]} />
                )}
            </div>
        );
    }

    // If feeds exist, show the property feed list
    return (
        <div className="tw-flex tw-flex-col tw-w-full">
            {/* Show message if there are feeds but no properties fetched */}
            {hasFeeds && !isLoading && combinedProperties.length === 0 && (
                <div className="tw-text-center tw-text-gray-500 tw-my-8">
                    No properties found for your current feed filters. Try adjusting your custom feed criteria.
                </div>
            )}
            {isLoading ? (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
                    {/* If top property is loaded, show it first, then skeletons */}
                    {(topPropertyData as any)?.data && plc ? (
                        <>
                            <SinglePropertyFeed property={(topPropertyData as any).data} feedType="following" />
                            {/* Show skeletons for remaining properties */}
                            {[...Array(2)].map((_, index) => (
                                <PropertyFeedSkeleton key={`skeleton-${index}`} />
                            ))}
                        </>
                    ) : (
                        // Show only skeletons if no top property
                        [...Array(3)].map((_, index) => <PropertyFeedSkeleton key={`skeleton-${index}`} />)
                    )}
                </div>
            ) : (
                <>
                    {combinedProperties.map((property: any) => (
                        <SinglePropertyFeed key={property.id} property={property} feedType="following" />
                    ))}

                    {hasNextPage && (
                        <div ref={loaderRef} className="tw-flex tw-items-center tw-justify-center tw-py-4">
                            <PropertyFeedSkeleton />
                        </div>
                    )}
                </>
            )}
            {/* Show the drawer if requested and no animation is showing */}
            {shouldShowDrawer && (
                <CustomFeedDrawer
                    show={true}
                    onHide={() => {
                        setDrawerOpen(false);
                        if (show && onHide) {
                            onHide();
                        }
                    }}
                    feeds={feeds as any[]}
                />
            )}
        </div>
    );
};

export default CustomFeed;
